import { cn } from '@/lib/utils';
import { BuilderMessage, BuilderArtifact } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface MessageBubbleProps {
  message: BuilderMessage;
  /** Whether this message is currently being streamed */
  isStreaming?: boolean;
  className?: string;
}

function ArtifactBadge({ artifact }: { artifact: BuilderArtifact }) {
  const typeLabels: Record<BuilderArtifact['type'], string> = {
    ui_definition: 'UI Update',
    workflow: 'Workflow',
    entity_selection: 'Entity',
    settings: 'Settings',
  };

  const typeColors: Record<BuilderArtifact['type'], string> = {
    ui_definition: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    workflow: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
    entity_selection: 'bg-green-500/10 text-green-600 border-green-500/20',
    settings: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  };

  return (
    <Badge variant="outline" className={cn('text-xs font-normal', typeColors[artifact.type])}>
      {typeLabels[artifact.type]}
    </Badge>
  );
}

export function MessageBubble({ message, isStreaming, className }: MessageBubbleProps) {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex w-full', isUser ? 'justify-end' : 'justify-start', className)}>
      <div
        className={cn(
          'max-w-[85%] rounded-lg px-4 py-3',
          isUser ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
        )}
      >
        <p className="whitespace-pre-wrap text-sm">
          {message.content}
          {isStreaming && <span className="inline-block w-2 h-4 ml-1 bg-current animate-pulse" />}
        </p>

        {/* Show artifacts if present */}
        {message.artifacts && message.artifacts.length > 0 && (
          <div className="mt-3 space-y-2">
            <div className="flex flex-wrap gap-1.5">
              {message.artifacts.map((artifact, index) => (
                <ArtifactBadge key={index} artifact={artifact} />
              ))}
            </div>
            {message.artifacts.some((a) => a.description) && (
              <Card className="bg-background/50">
                <CardContent className="p-2 text-xs text-muted-foreground">
                  {message.artifacts
                    .filter((a) => a.description)
                    .map((artifact, index) => (
                      <div key={index}>
                        <span className="font-medium">{artifact.type.replace('_', ' ')}:</span>{' '}
                        {artifact.description}
                      </div>
                    ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Timestamp - hide while streaming or when timestamp is missing/invalid */}
        {!isStreaming && message.timestamp && !isNaN(new Date(message.timestamp).getTime()) && (
          <div
            className={cn(
              'mt-1 text-[10px]',
              isUser ? 'text-primary-foreground/70' : 'text-muted-foreground',
            )}
          >
            {new Date(message.timestamp).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        )}
      </div>
    </div>
  );
}
